import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { useSoftDeletePaymentGateway } from '../api/mutations.js';
import { paymentGatewayEndpoints } from '../api/endpoints.js';
import { paymentGatewayRow, PROVIDER_OPTIONS, type PaymentGatewayRow } from '../api/schemas.js';

/**
 * The payment gateways list.
 *
 * Search, sort, pagination and the three non-data states all come from
 * `ResourceListPage`. What is here is the descriptor and the row actions.
 */

const COLUMNS: readonly ColumnDescriptor<PaymentGatewayRow>[] = [
  {
    id: 'name',
    header: 'Gateway',
    sortable: true,
    alwaysVisible: true,
    fields: ['name'],
    cell: (row) => <PrimaryCell label={row.name ?? row.id} to={`/payment-gateways/${row.id}`} />,
  },
  {
    id: 'provider',
    header: 'Provider',
    sortable: true,
    fields: ['provider'],
    cell: (row) => row.provider,
  },
  {
    id: 'default',
    header: 'Default',
    fields: ['isDefault'],
    cell: (row) => (row.isDefault ? 'Yes' : 'No'),
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    fields: ['isActive'],
    cell: (row) =>
      row.isActive === undefined ? null : (
        <StatusBadge tone={toneForStatus(row.isActive ? 'ACTIVE' : 'INACTIVE')}>
          {row.isActive ? 'Active' : 'Inactive'}
        </StatusBadge>
      ),
  },
  {
    id: 'createdAt',
    header: 'Created',
    sortable: true,
    fields: ['createdAt'],
    cell: (row) =>
      row.createdAt === undefined ? null : new Date(row.createdAt).toLocaleDateString('en-AU'),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['name'],
    cell: (row: PaymentGatewayRow): React.JSX.Element => <PaymentGatewayRowActions row={row} />,
  },
];

const RESOURCE = 'paymentGateways';

const FILTERS = [
  {
    id: 'provider',
    label: 'Provider',
    param: 'provider',
    kind: 'select' as const,
    options: PROVIDER_OPTIONS,
  },
  {
    id: 'isDefault',
    label: 'Default',
    param: 'isDefault',
    kind: 'boolean' as const,
  },
];

function PaymentGatewayRowActions({ row }: { readonly row: PaymentGatewayRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = useSoftDeletePaymentGateway();
  const label = row.name ?? row.id;

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/payment-gateways/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/payment-gateways/${row.id}/edit` },
          ]}
        />
        <PermissionGuard permission={PERMISSIONS.PAYMENT_CONFIGURE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete gateway"
        description={`${label} will be removed. Its audit history is retained.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

export function PaymentGatewayListPage(): React.JSX.Element {
  return (
    <ResourceListPage
      title="Payment gateways"
      description="The providers this tenant takes payment through."
      path={paymentGatewayEndpoints.list}
      resource={RESOURCE}
      schema={paymentGatewayRow}
      columns={COLUMNS}
      filters={FILTERS}
      rowKey={(row) => row.id}
      permission={PERMISSIONS.PAYMENT_READ}
      actions={
        <PermissionGuard permission={PERMISSIONS.PAYMENT_CONFIGURE}>
          <Button asChild>
            <Link to="/payment-gateways/new">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add gateway
            </Link>
          </Button>
        </PermissionGuard>
      }
    />
  );
}
